import React, { useCallback, useEffect, useRef, useState } from "react";
import { PaperPlaneRight, Plus, Sparkle, Lightning, Paperclip, X, ThumbsUp, ThumbsDown, FileText, Image as ImageIcon, Microphone } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import Markdown from "@/components/Markdown";

const EFFORTS = [
  { id: "instant", label: "Instant" },
  { id: "balanced", label: "Balanced" },
  { id: "deep", label: "Deep" },
  { id: "ultra", label: "Ultra" },
];

export default function Workspace() {
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [effort, setEffort] = useState("balanced");
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);

  const loadProjects = useCallback(async () => {
    try { const r = await api.get("/projects"); setProjects(r.data); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    api.get("/models").then((r) => setModels(r.data)).catch(() => {});
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const openProject = async (pid) => {
    setProjectId(pid);
    try { const r = await api.get(`/projects/${pid}/messages`); setMessages(r.data); } catch (e) { console.error(e); }
  };

  const newChat = () => { setProjectId(null); setMessages([]); setAttachments([]); };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, { upload_id: r.data.upload_id, filename: r.data.filename, kind: r.data.kind }]);
      } catch { toast.error(`Failed to upload ${f.name}`); }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const rate = async (rating) => {
    try { await api.post("/feedback", { rating, context: "workspace" }); toast.success(rating === "up" ? "Thanks — learning from this" : "Noted — improving"); } catch (e) { console.error(e); }
  };

  const toggleMic = async () => {
    if (recording) { recorderRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        if (!chunksRef.current.length) { toast.error("Nothing recorded"); return; }
        setTranscribing(true);
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const r = await api.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setInput((prev) => (prev ? prev + " " : "") + (r.data.text || ""));
          if (r.data.text) toast.success("Transcribed"); else toast.error("Nothing heard");
        } catch { toast.error("Transcription failed"); }
        finally { setTranscribing(false); }
      };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
    } catch { toast.error("Microphone permission needed"); }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    const atts = attachments;
    const attLabel = atts.length ? "\n\n" + atts.map((a) => `📎 ${a.filename}`).join("\n") : "";
    setMessages((m) => [...m, { role: "user", content: text + attLabel }, { role: "assistant", content: "" }]);
    setStreaming(true);
    setAttachments([]);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: projectId, message: text, model, effort, attachments: atts.map((a) => a.upload_id) }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let newPid = projectId;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.project_id && !newPid) { newPid = evt.project_id; setProjectId(evt.project_id); }
            if (evt.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + evt.delta };
                return copy;
              });
            }
          } catch (e) { console.error(e); }
        }
      }
      if (!projectId && newPid) loadProjects();
    } catch (e) {
      toast.error("Failed to reach the model. Try again.");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setStreaming(false);
    }
  };

  const modelsByFamily = models.reduce((acc, m) => { (acc[m.family] = acc[m.family] || []).push(m); return acc; }, {});

  return (
    <div className="flex h-screen">
      {/* Projects rail */}
      <div className="w-64 shrink-0 border-r border-[#2A2625]/10 bg-[#F9F6F4] flex flex-col">
        <div className="h-16 flex items-center px-4 border-b border-[#2A2625]/10">
          <button
            data-testid="new-chat-btn"
            onClick={newChat}
            className="w-full flex items-center gap-2 justify-center bg-[#FFB8A5] hover:bg-[#FFCBBF] text-[#2A2625] font-medium rounded-full py-2.5 text-sm transition-colors"
          >
            <Plus size={16} weight="bold" /> New chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {projects.length === 0 && <p className="text-xs text-[#8C8381] px-2 py-3">No chats yet.</p>}
          {projects.map((p) => (
            <button
              key={p.project_id}
              data-testid={`project-${p.project_id}`}
              onClick={() => openProject(p.project_id)}
              className={`w-full text-left px-3 py-2.5 rounded-xl text-sm truncate transition-colors ${
                projectId === p.project_id ? "bg-[#FFB8A5]/40 text-[#2A2625]" : "text-[#594F4D] hover:bg-[#2A2625]/5"
              }`}
            >
              {p.title || "Untitled"}
            </button>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* control bar */}
        <div className="h-16 border-b border-[#2A2625]/10 bg-white/70 backdrop-blur-xl flex items-center gap-3 px-6">
          <select
            data-testid="model-select"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="bg-[#FCFAF8] border border-[#2A2625]/15 rounded-lg px-3 py-2 text-sm font-medium outline-none focus:ring-2 focus:ring-[#FFB8A5]"
          >
            {Object.entries(modelsByFamily).map(([fam, list]) => (
              <optgroup key={fam} label={fam}>
                {list.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </optgroup>
            ))}
          </select>
          <div className="flex items-center gap-1 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-lg p-1">
            {EFFORTS.map((e) => (
              <button
                key={e.id}
                data-testid={`effort-${e.id}`}
                onClick={() => setEffort(e.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${
                  effort === e.id ? "bg-[#FFB8A5] text-[#2A2625]" : "text-[#594F4D] hover:text-[#2A2625]"
                }`}
              >
                {e.id === "ultra" && <Lightning size={12} weight="fill" />} {e.label}
              </button>
            ))}
          </div>
        </div>

        {/* messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-8">
          <div className="max-w-3xl mx-auto space-y-6">
            {messages.length === 0 && (
              <div className="text-center pt-16">
                <div className="w-14 h-14 rounded-2xl bg-[#FFF4F0] flex items-center justify-center mx-auto mb-5">
                  <Sparkle size={28} weight="duotone" className="text-[#F29A86]" />
                </div>
                <h2 className="font-display text-2xl font-semibold">Zeyrodha Workspace</h2>
                <p className="text-[#594F4D] mt-2">Ask anything. Switch models and effort levels on the fly.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`message-${m.role}`}>
                <div className={`max-w-[85%] rounded-2xl px-5 py-3.5 ${
                  m.role === "user" ? "bg-[#FFB8A5]/25 text-[#2A2625]" : "bg-white border border-[#2A2625]/10"
                }`}>
                  {m.role === "assistant" ? (
                    m.content ? (
                      <>
                        <Markdown>{m.content}</Markdown>
                        {!streaming && (
                          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#2A2625]/5">
                            <button data-testid="feedback-up" onClick={() => rate("up")} className="text-[#8C8381] hover:text-[#A3C2A4] transition-colors"><ThumbsUp size={15} /></button>
                            <button data-testid="feedback-down" onClick={() => rate("down")} className="text-[#8C8381] hover:text-[#E09494] transition-colors"><ThumbsDown size={15} /></button>
                          </div>
                        )}
                      </>
                    ) : <span className="inline-block w-2 h-4 bg-[#F29A86] z-caret align-middle" />
                  ) : (
                    <p className="whitespace-pre-wrap">{m.content}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* composer */}
        <div className="border-t border-[#2A2625]/10 bg-white p-4">
          <div className="max-w-3xl mx-auto">
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {attachments.map((a, i) => (
                  <span key={a.upload_id} className="flex items-center gap-1.5 text-xs bg-[#FFF4F0] border border-[#2A2625]/10 rounded-full px-3 py-1.5" data-testid={`attachment-chip-${i}`}>
                    {a.kind === "image" ? <ImageIcon size={13} /> : <FileText size={13} />}
                    <span className="max-w-[160px] truncate">{a.filename}</span>
                    <X size={13} className="cursor-pointer hover:text-[#E09494]" onClick={() => setAttachments((list) => list.filter((_, idx) => idx !== i))} />
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-end gap-3">
              <input ref={fileRef} type="file" multiple className="hidden" onChange={onUpload} accept="image/*,.pdf,.txt,.csv,.md,.json,.docx,.xlsx,video/*" />
              <button data-testid="attach-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="w-11 h-11 shrink-0 rounded-2xl border border-[#2A2625]/15 hover:bg-[#2A2625]/5 disabled:opacity-40 flex items-center justify-center transition-colors">
                {uploading ? <span className="w-4 h-4 rounded-full border-2 border-[#F29A86] border-t-transparent animate-spin" /> : <Paperclip size={18} className="text-[#594F4D]" />}
              </button>
              <button data-testid="mic-btn" onClick={toggleMic} disabled={transcribing}
                className={`w-11 h-11 shrink-0 rounded-2xl border flex items-center justify-center transition-colors ${recording ? "bg-[#E09494]/20 border-[#E09494] animate-pulse" : "border-[#2A2625]/15 hover:bg-[#2A2625]/5"} disabled:opacity-40`}>
                {transcribing ? <span className="w-4 h-4 rounded-full border-2 border-[#F29A86] border-t-transparent animate-spin" /> : <Microphone size={18} weight={recording ? "fill" : "regular"} className={recording ? "text-[#E09494]" : "text-[#594F4D]"} />}
              </button>
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1}
                placeholder="Message Zeyrodha…"
                className="flex-1 resize-none bg-[#FCFAF8] border border-[#2A2625]/15 rounded-2xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5] max-h-40"
              />
              <button
                data-testid="send-btn"
                onClick={send}
                disabled={streaming || !input.trim()}
                className="w-11 h-11 shrink-0 rounded-2xl bg-[#FFB8A5] hover:bg-[#FFCBBF] disabled:opacity-40 flex items-center justify-center transition-colors"
              >
                <PaperPlaneRight size={18} weight="fill" className="text-[#2A2625]" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
